import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Empty, Input, Modal, Popconfirm, Select, Tag, Typography } from 'antd';

import ItemFilterBuilder from '../items/ItemFilterBuilder';
import ItemFilterChips from '../items/ItemFilterChips';
import QueryRefusalPick from '../items/QueryRefusalPick';
import useElementChoices from '../../hooks/useElementChoices';
import ShareModal from '../common/ShareModal';
import { DataState, Skeleton, deriveDataState } from '../loading';
import { CHART_LINE, CLOSE, EDIT, FUNNEL, PLUS } from '../icons';
import IntakeLinkModal from './IntakeLinkModal';
import PreparedFormModal from './PreparedFormModal';
import { retirePublicForm } from '../../services/publicForms';
import { describePublicFormError, formStatus } from '../../utils/publicForms';
import { labelOf as elementLabelOf } from '../../utils/intakeLinks';
import ReviewRulesModal from './ReviewRulesModal';
import { reviewSummary } from '../../utils/reviewRules';
import FormVolume from './FormVolume';
import IncludeFilesField, { RestrictedFilesNote } from '../common/IncludeFilesField';
import ToggleRow from '../common/ToggleRow';
import {
	createShareLink,
	describeShareLinkError,
	getShareLinks,
	kindTakesFiles,
	linkAtDailyFileLimit,
	linkIncludesFiles,
	linkKind,
	previewShareLinkFiles,
	readFilesCount,
	revokeShareLink,
	setShareLinkIncludeFiles,
	DEFAULT_EXPIRY_DAYS,
	EXPIRY_OPTIONS,
	ONE_TIME_CODE_NOTE,
} from '../../services/shareLinks';
import { buildShortLinkUrl } from '../../utils/shareUrls';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import { includesFilesText } from '../../utils/publicFiles';
import { negatedTagWarningText } from '../../utils/tags';
import { destructivePopconfirmProps } from '../common/confirmDestructive';

const { Text } = Typography;

const SHARE_LINK_ME_NOTE = 'A public link\'s filter cannot use me: the people opening it are not signed in.';

const STATUS_TAGS = {
	active: { color: 'green', label: 'Active' },
	revoked: { color: 'red', label: 'Revoked' },
	expired: { color: 'default', label: 'Expired' },
	// An intake link whose submissions reached its cap: still listed, still
	// revocable, answering the flat 404 to visitors until it is edited.
	full: { color: 'gold', label: 'Full' },
};

// What a row is a link TO. Links minted from the share modals land in this
// same listing, so the kind has to be legible at a glance.
const KIND_TAGS = {
	view: { color: 'blue', label: 'View' },
	item: { color: 'purple', label: 'Item' },
	card: { color: 'geekblue', label: 'Card' },
	// The one kind that WRITES — a manager scanning the list must tell it
	// from the reading kinds at a glance.
	intake: { color: 'orange', label: 'Intake' },
};

// The one-line "which records" summary for a row with no query to chip.
const KIND_SUMMARY = {
	view: 'Every item in this collection',
	item: 'One item',
	card: 'One card definition',
	intake: 'A form anyone with the link can send',
};

/**
 * Temporary share links for one collection (manage-gated, like the rest of the
 * Sharing tab).
 *
 * A link pins a target: anyone holding the code reads it with NO account until
 * it expires or is revoked. The raw code exists exactly once, in the create
 * response — the listing below can show who shared what and when, but never the
 * code itself, because only its hash is stored. A manager who loses a link
 * revokes it and makes another.
 *
 * The form here makes VIEW links, and its filter is optional: no filter means
 * the whole collection, which is a deliberate choice a manager can now make in
 * one place instead of faking with a match-everything query. Item and card
 * links are minted from their own share modals (TempShareLinkSection) and show
 * up in this listing too, tagged by kind and revocable like any other.
 *
 * INTAKE links — public forms that create items — are made and edited here
 * too (IntakeLinkModal), once the collection's Intake forms switch is on. Their
 * rows say which card they fill in and how many of their submissions are
 * used; a link that reached its cap reads **Full**.
 *
 * FILES (PR 10c): a live view or item link has its own Include files switch
 * (a PATCH; links made before 10c start off, G12). Turning it on says what it
 * now discloses — the count, and the G15 note when some files are listed by
 * name only. A row that includes files says **Files included**, and one whose
 * files spent today's byte budget says **Daily file limit reached**.
 *
 * VOLUME (KPR-513): where the API counts submissions (`formVolume`), an
 * intake link's row offers Show volume — its submissions, accepts and
 * rejects over time, drawn under the row (FormVolume), as a permanent public
 * form's are on the Public forms tab.
 *
 * REVIEW RULES (KPR-482): where the server's public forms are switched on
 * (`reviewRules`), a live intake link's row offers its accept and reject
 * rules (ReviewRulesModal, the public form's own editor), and says how many
 * it has — and when one stopped working.
 *
 * PREPARED FORMS (keepr-api KPR-360): an intake form a package brought is an
 * invite link's DEFINITION, prepared and left off — `preparedForms`, the
 * collection's invite-drafts (`GET …/public-forms`, read once by the settings
 * page). Each row says **Not live**; **Create link…** shows what the link
 * would expose in plain words and only then makes an ordinary intake link
 * from it (PreparedFormModal), its one-time code revealed as any other's;
 * **Change it first…** opens the intake editor seeded with it. The draft stays
 * to make the next link from, until Remove (it is retired). While Intake
 * forms are off, Create link waits, and says to turn them on above.
 */
export default function ShareLinksPanel({
	collectionId,
	filterFields = [],
	allowIntakeLinks = false,
	cardOptions = [],
	cardsById = {},
	getElementsForCard = null,
	formVolume = false,
	reviewRules = false,
	preparedForms = [],
	onPreparedChanged = null,
}) {
	const { message } = App.useApp();
	// KPR-360: the prepared form whose "Create link" confirmation is open.
	const [preparedFor, setPreparedFor] = useState(null);
	const [removingPrepared, setRemovingPrepared] = useState(null);
	// The intake link whose rules are open in the editor.
	const [rulesFor, setRulesFor] = useState(null);
	const [links, setLinks] = useState([]);
	// The intake links whose volume is open under their row.
	const [volumeOpen, setVolumeOpen] = useState({});
	const [loading, setLoading] = useState(true);
	// The first read has answered (a failed one is an error with Retry, never an empty list).
	const [loaded, setLoaded] = useState(false);
	const [loadError, setLoadError] = useState(false);
	const [formOpen, setFormOpen] = useState(false);
	const [creating, setCreating] = useState(false);
	const [name, setName] = useState('');
	const [query, setQuery] = useState('');
	const [expiresInDays, setExpiresInDays] = useState(DEFAULT_EXPIRY_DAYS);
	// "Include files" (PR 10c): on for every new link (G12 keeps old ones off).
	const [includeFiles, setIncludeFiles] = useState(true);
	// The one-time reveal: {code, url, name, expiresAt}.
	const [created, setCreated] = useState(null);
	// The intake modal: { link: null } to make one, { link } to edit one.
	const [intakeEditor, setIntakeEditor] = useState(null);
	// Include files on a listed link: the row being saved, and what each link
	// turned on just now discloses ({ [linkId]: { total, restricted } }).
	const [filesBusy, setFilesBusy] = useState(null);
	const [filesNotes, setFilesNotes] = useState({});
	const cardNameById = useMemo(
		() => new Map(cardOptions.map(option => [option.value, option.card?.name || 'Untitled card'])),
		[cardOptions]
	);

	const reload = useCallback(() => getShareLinks(collectionId)
		.then(setLinks)
		.catch(() => message.error('Could not load share links.')), [collectionId, message]);

	const [attempt, setAttempt] = useState(0);
	useEffect(() => {
		if (!collectionId) return undefined;
		let active = true;
		setLoading(true);
		getShareLinks(collectionId)
			.then(list => { if (active) { setLinks(list); setLoaded(true); setLoadError(false); } })
			.catch(() => { if (active) setLoadError(true); })
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	}, [collectionId, attempt]);

	// An element name the filter could mean several of (400
	// `ambiguous_element`, element ids step 3): the person picks one, or any
	// of these cards, right in the window, and the link is made again with
	// the pick. A new filter starts without picks.
	const elementPicks = useElementChoices(query || '');
	const resetElementPicks = elementPicks.reset;
	useEffect(() => { resetElementPicks(); }, [query, resetElementPicks]);

	const closeForm = useCallback(() => {
		setFormOpen(false);
		setName('');
		setQuery('');
		setExpiresInDays(DEFAULT_EXPIRY_DAYS);
		setIncludeFiles(true);
		resetElementPicks();
	}, [resetElementPicks]);

	const handleCreate = useCallback(async () => {
		if (!collectionId) return;
		setCreating(true);
		try {
			// No query is a valid link now: the whole collection, unfiltered.
			const link = await createShareLink(collectionId, elementPicks.withChoices({
				kind: 'view',
				query: query || undefined,
				name: name.trim() || undefined,
				expiresInDays,
				includeFiles,
			}));
			closeForm();
			// `url` is null when the API has no APP_BASE_URL configured; the
			// code always comes back, so this origin can build the link.
			setCreated({ ...link, url: link?.url || buildShortLinkUrl(link?.code) });
			// A filter that leaves items out by a restricted tag saves, with
			// the API's warning (T7, `negated_restricted_tag`), as given.
			const warning = negatedTagWarningText(link?.warnings);
			if (warning) message.warning(warning);
			await reload();
		} catch (error) {
			if (!elementPicks.take(error)) message.error(describeShareLinkError(error));
		} finally {
			setCreating(false);
		}
	}, [collectionId, query, name, expiresInDays, includeFiles, closeForm, reload, message, elementPicks]);

	// KPR-360: an element of a prepared form's card, named as the card labels it.
	const labelFor = useCallback((form) => {
		const elements = getElementsForCard ? (getElementsForCard(normalizeId(form?.card_id)) || []) : [];
		const byName = new Map(elements.map(el => [el?.name, el]));
		return name => (byName.has(name) ? elementLabelOf(byName.get(name)) : name);
	}, [getElementsForCard]);
	// The Sharing tab is manager-only, and any manager turns intake forms on
	// (the owner's ruling of 2026-10-25: managers have the owner's permissions
	// except transfer), so the reason is always the same next step.
	const preparedDisabled = allowIntakeLinks ? null : 'Turn on Intake forms above to create its link.';

	const handlePreparedCreated = useCallback(async (result) => {
		setPreparedFor(null);
		setCreated({ ...result, url: result?.url || buildShortLinkUrl(result?.code) });
		await reload();
	}, [reload]);

	const handleRemovePrepared = useCallback(async (form) => {
		const id = normalizeId(form?._id);
		setRemovingPrepared(id);
		try {
			await retirePublicForm(collectionId, id);
			message.success(`${form?.name || 'The prepared form'} is removed.`);
		} catch (error) {
			message.error(describePublicFormError(error, {}, 'The prepared form could not be removed.'));
		} finally {
			setRemovingPrepared(null);
			await onPreparedChanged?.();
		}
	}, [collectionId, message, onPreparedChanged]);

	const handleIntakeSaved = useCallback(async (result, { created: isNew } = {}) => {
		setIntakeEditor(null);
		if (isNew) {
			setCreated({ ...result, url: result?.url || buildShortLinkUrl(result?.code) });
		} else {
			message.success('Intake link saved.');
		}
		await reload();
	}, [reload, message]);

	const handleRulesSaved = useCallback(async (saved) => {
		setRulesFor(null);
		const { count } = reviewSummary(saved?.review);
		message.success(count ? `${saved?.name || 'The link'} decides by ${count === 1 ? '1 rule' : `${count} rules`}.` : `People review every submission to ${saved?.name || 'the link'}.`);
		await reload();
	}, [reload, message]);

	const handleIncludeFiles = useCallback(async (link, next) => {
		const id = normalizeId(link?._id);
		setFilesBusy(id);
		setFilesNotes(({ [id]: _dropped, ...rest }) => rest);
		try {
			const saved = await setShareLinkIncludeFiles(collectionId, id, next);
			setLinks(current => current.map(row => (normalizeId(row?._id) === id
				? { ...row, ...(saved && typeof saved === 'object' ? saved : {}), includeFiles: next }
				: row)));
			if (!next) return;
			// The owner's G15 note: on, the sharer is told what is now out there.
			const count = readFilesCount(saved?.files)
				|| await previewShareLinkFiles(collectionId, { link_id: id }).catch(() => null);
			if (count) setFilesNotes(current => ({ ...current, [id]: count }));
		} catch (error) {
			message.error(describeShareLinkError(error, 'Could not change the link.'));
		} finally {
			setFilesBusy(null);
		}
	}, [collectionId, message]);

	const handleRevoke = useCallback(async (linkId) => {
		try {
			await revokeShareLink(collectionId, linkId);
		} catch (error) {
			message.error(error?.response?.data?.message || 'Could not revoke the link.');
		}
		await reload();
	}, [collectionId, reload, message]);

	return (
		<section className="sharing-tab__section share-links">
			<h3>Share links</h3>
			<Text type="secondary">
				A temporary, public link to items in this collection — a filtered set, or all
				of them. No account needed: anyone with the link can read them until it
				expires or you revoke it. An intake link works the other way round: a form
				anyone with the link can send you an item through.
			</Text>

			{preparedForms.length ? (
				<div className="prepared-forms" data-testid="prepared-forms">
					<div className="micro-label prepared-forms__label">Prepared forms</div>
					<p className="share-links__hint">
						{preparedForms.length === 1
							? 'A package brought this intake form. It is not live: nobody can use it until you create its link.'
							: 'A package brought these intake forms. None is live: nobody can use one until you create its link.'}
					</p>
					<div className="share-links__list">
						{preparedForms.map((form) => {
							const id = normalizeId(form?._id);
							const status = formStatus(form);
							const cap = form?.invite?.maxSubmissions;
							const days = form?.invite?.expiresInDays;
							return (
								<div className="share-links__row" key={id} data-testid="prepared-form-row">
									<div className="share-links__row-main">
										<span className="share-links__name">{form?.name || 'Untitled form'}</span>
										<span className="share-links__query share-links__summary">
											{`A form for ${cardNameById.get(normalizeId(form?.card_id)) || 'a card'}`}
											{Number.isInteger(cap) && Number.isInteger(days) ? ` · stops after ${cap} submissions or ${days} days` : ''}
										</span>
									</div>
									<div className="share-links__row-meta">
										<Tag className="share-links__status" data-testid="prepared-form-status">{status.label}</Tag>
									</div>
									<Button
										className="prepared-forms__create"
										onClick={() => setPreparedFor(form)}
										aria-label={`Create a link from ${form?.name || 'this form'}`}
										data-action="prepared-form-open"
									>
										Create link…
									</Button>
									<Popconfirm
										title="Remove this prepared form?"
										description="Links already made from it keep working. You can't bring it back."
										{...destructivePopconfirmProps('Remove')}
										onConfirm={() => handleRemovePrepared(form)}
									>
										<Button
											type="text"
											danger
											icon={<CLOSE />}
											loading={removingPrepared === id}
											aria-label={`Remove prepared form ${form?.name || 'Untitled form'}`}
										/>
									</Popconfirm>
								</div>
							);
						})}
					</div>
				</div>
			) : null}

			<DataState
				state={deriveDataState({ loading, error: loadError, hasData: loaded })}
				hasData={loaded}
				skeleton={<Skeleton variant="list-row" count={3} />}
				onRetry={() => setAttempt(n => n + 1)}
				label="share links"
			>
				{links.length === 0
					? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No share links yet" />
					: (
						<div className="share-links__list">
							{links.map(link => {
								const id = normalizeId(link?._id);
								const status = STATUS_TAGS[link?.status] || STATUS_TAGS.expired;
								const kind = linkKind(link);
								const kindTag = KIND_TAGS[kind] || KIND_TAGS.view;
								const isIntake = kind === 'intake';
								const editable = isIntake && (link?.status === 'active' || link?.status === 'full' || link?.status === 'expired');
								return (
									<div className="share-links__row" key={id}>
										<div className="share-links__row-main">
											<span className="share-links__name">{link?.name || 'Untitled link'}</span>
											{/* Only a view link with a stored filter has chips to
											    show; everything else says what it points at. */}
											{link?.query ? (
												<ItemFilterChips
													readOnly
													query={link.query}
													fields={filterFields}
													className="share-links__query"
												/>
											) : isIntake ? (
												<span className="share-links__query share-links__summary" data-testid="intake-link-summary">
													{`A form for ${cardNameById.get(normalizeId(link?.card_id)) || 'a card'} · `}
													{`${link?.submissionCount || 0} of ${link?.maxSubmissions || 0} submissions`}
												</span>
											) : (
												<span className="share-links__query share-links__summary">
													{KIND_SUMMARY[kind] || KIND_SUMMARY.view}
												</span>
											)}
										</div>
										<div className="share-links__row-meta">
											<Tag color={kindTag.color} className="share-links__kind">{kindTag.label}</Tag>
											<Tag color={status.color} className="share-links__status">{status.label}</Tag>
											{linkIncludesFiles(link) ? <Tag className="share-links__files-tag">Files included</Tag> : null}
											{isIntake && reviewSummary(link?.review).count ? (
												<Tag className="share-links__rules-tag" data-testid="intake-link-rules">{reviewSummary(link.review).count === 1 ? '1 rule' : `${reviewSummary(link.review).count} rules`}</Tag>
											) : null}
											{isIntake && reviewSummary(link?.review).broken ? (
												<Tag color="gold" className="share-links__rules-tag" title="A rule stopped working: it and the rules after it decide nothing.">A rule stopped working</Tag>
											) : null}
											{linkAtDailyFileLimit(link) ? <Tag color="gold" className="share-links__files-tag">Daily file limit reached</Tag> : null}
											<span className="share-links__expiry">
												{link?.status === 'revoked'
													? `Revoked ${formatDisplayDate(link.revokedAt)}`
													: `Expires ${formatDisplayDate(link?.expiresAt)}`}
											</span>
										</div>
										{editable ? (
											<Button
												type="text"
												icon={<EDIT />}
												aria-label={`Edit intake link ${link?.name || 'Untitled link'}`}
												onClick={() => setIntakeEditor({ link })}
											/>
										) : null}
										{isIntake && reviewRules && (link?.status === 'active' || link?.status === 'full') ? (
											<Button
												type="text"
												icon={<FUNNEL />}
												aria-label={`Review rules of ${link?.name || 'Untitled link'}`}
												title="Review rules"
												onClick={() => setRulesFor(link)}
												data-action="intake-link-rules"
											/>
										) : null}
										{isIntake && formVolume ? (
											<Button
												type="text"
												icon={<CHART_LINE />}
												aria-label={`${volumeOpen[id] ? 'Hide' : 'Show'} volume of ${link?.name || 'Untitled link'}`}
												aria-expanded={Boolean(volumeOpen[id])}
												title={volumeOpen[id] ? 'Hide volume' : 'Show volume'}
												onClick={() => setVolumeOpen(open => ({ ...open, [id]: !open[id] }))}
												data-action="intake-link-volume"
											/>
										) : null}
										{link?.status === 'active' || link?.status === 'full' ? (
											<Popconfirm
												title="Revoke this link?"
												description="Anyone holding it loses access immediately, and it cannot be restored."
												{...destructivePopconfirmProps('Revoke')}
												onConfirm={() => handleRevoke(id)}
											>
												<Button
													type="text"
													danger
													icon={<CLOSE />}
													aria-label={`Revoke share link ${link?.name || 'Untitled link'}`}
												/>
											</Popconfirm>
										) : null}
										{/* A live view or item link turns its files on or off
										    here; a card or intake link carries none. */}
										{kindTakesFiles(kind) && link?.status === 'active' ? (
											<div className="share-links__files">
												<ToggleRow
													first
													label="Include files"
													aria-label={`Include files in ${link?.name || 'Untitled link'}`}
													checked={linkIncludesFiles(link)}
													disabled={filesBusy === id}
													onChange={next => handleIncludeFiles(link, next)}
													size="small"
												/>
												{filesNotes[id] ? (
													<>
														<p className="include-files__note">{includesFilesText(filesNotes[id])}</p>
														<RestrictedFilesNote count={filesNotes[id]} />
													</>
												) : null}
											</div>
										) : null}
										{isIntake && formVolume && volumeOpen[id] ? (
											<div className="share-links__volume">
												<FormVolume collectionId={collectionId} source={{ kind: 'link', id }} name={link?.name || null} />
											</div>
										) : null}
									</div>
								);
							})}
						</div>
					)
			}
			</DataState>

			<div className="share-links__new-row">
				<Button
					className="share-links__new"
					icon={<PLUS />}
					onClick={() => setFormOpen(true)}
				>
					New share link
				</Button>
				{allowIntakeLinks ? (
					<Button
						className="share-links__new"
						icon={<PLUS />}
						onClick={() => setIntakeEditor({ link: null })}
						data-action="new-intake-link"
					>
						New intake link
					</Button>
				) : null}
			</div>
			{!allowIntakeLinks ? (
				<p className="share-links__hint">Turn on <strong>Intake forms</strong> above to make a form link people can send items through.</p>
			) : null}

			<ReviewRulesModal
				open={Boolean(rulesFor)}
				onClose={() => setRulesFor(null)}
				onSaved={handleRulesSaved}
				collectionId={collectionId}
				source={rulesFor ? { kind: 'link', id: normalizeId(rulesFor._id) } : null}
				name={rulesFor?.name || ''}
				definition={rulesFor}
				review={rulesFor?.review || null}
				cardId={rulesFor?.card_id || null}
				cardsById={cardsById}
				getElementsForCard={getElementsForCard}
			/>

			<PreparedFormModal
				open={Boolean(preparedFor)}
				form={preparedFor}
				collectionId={collectionId}
				cardName={preparedFor ? (cardNameById.get(normalizeId(preparedFor.card_id)) || null) : null}
				labelOf={preparedFor ? labelFor(preparedFor) : undefined}
				disabledReason={preparedDisabled}
				onClose={() => setPreparedFor(null)}
				onCreated={handlePreparedCreated}
				onChangeFirst={preparedDisabled ? null : (form) => { setPreparedFor(null); setIntakeEditor({ link: null, template: form }); }}
			/>

			<IntakeLinkModal
				open={Boolean(intakeEditor)}
				link={intakeEditor?.link || null}
				template={intakeEditor?.template || null}
				onClose={() => setIntakeEditor(null)}
				onSaved={handleIntakeSaved}
				collectionId={collectionId}
				cardOptions={cardOptions}
				cardsById={cardsById}
				getElementsForCard={getElementsForCard}
			/>

			<Modal
				open={formOpen}
				title="New share link"
				okText="Create link"
				confirmLoading={creating}
				onOk={handleCreate}
				onCancel={closeForm}
				destroyOnClose
				className="share-links__modal"
			>
				<label className="share-links__field-label" htmlFor="share-link-name">Name (optional)</label>
				<Input
					id="share-link-name"
					maxLength={100}
					value={name}
					placeholder="e.g. Molly's family"
					onChange={event => setName(event.target.value)}
				/>

				<div className="share-links__field">
					<span className="share-links__field-label">Which items</span>
					<ItemFilterBuilder
						fields={filterFields}
						query={query}
						onApply={text => setQuery(text || '')}
						// A public link decides who sees what, and the server
						// reads its names in every sub-collection, and asks (⚑ K4).
						keepElementNames
						// No one is signed in on a public link, so `me` has no meaning.
						disallowMe
						meDisallowedNote={SHARE_LINK_ME_NOTE}
					/>
					<QueryRefusalPick
						inline
						refusal={elementPicks.refusal}
						disabled={creating}
						onPick={(value) => { elementPicks.pick(value); handleCreate(); }}
						onRepick={handleCreate}
						access={query}
					/>
					{query ? (
						<ItemFilterChips
							readOnly
							query={query}
							fields={filterFields}
							className="share-links__query"
						/>
					) : (
						<p className="share-links__hint">
							No filter — this link shares <strong>every item</strong> in the collection.
							Add one to narrow what a visitor can see.
						</p>
					)}
				</div>

				<div className="share-links__field">
					<label className="share-links__field-label" htmlFor="share-link-expiry">Expires after</label>
					<Select
						id="share-link-expiry"
						className="share-links__expiry-select"
						value={expiresInDays}
						options={EXPIRY_OPTIONS}
						onChange={setExpiresInDays}
					/>
				</div>

				<IncludeFilesField
					className="share-links__field"
					collectionId={collectionId}
					kind="view"
					query={query}
					checked={includeFiles}
					onChange={setIncludeFiles}
				/>
			</Modal>

			<ShareModal
				open={!!created}
				onClose={() => setCreated(null)}
				modalTitle="Share link created"
				previewTitle={created?.name || 'Share link'}
				eyebrow={created?.expiresAt ? `Expires ${formatDisplayDate(created.expiresAt)}` : 'Temporary link'}
				shareUrl={created?.url || ''}
				note={ONE_TIME_CODE_NOTE}
				downloadFileName="keepr-share-link"
				className="share-links__share-modal"
			/>
		</section>
	);
}
